import {useFieldArray, useFormContext} from "react-hook-form";
import {FormControl, FormField, FormItem, FormMessage} from "@/components/ui/form.tsx";
import {Input} from "@/components/ui/input.tsx";
import NumberInput from "@/components/forms/NumberInput.tsx";
import {calculateModifier} from "@/characters/model/AttributeStat.ts";
import {AttributeStat} from "@/characters/model/Character.ts";
import {CharacterFormInput} from "@/characters/admin/form/CharacterFormSchema.ts";
import FormSection from "@/components/forms/FormSection.tsx";
import {ReactNode} from "react";

export default function CharacterStatsForm() {
    const {control, setValue} = useFormContext<CharacterFormInput>();
    const attributes = useFieldArray({control, name: "attributes"});

    return <div className="grid items-start gap-6 grid-cols-1 xl:grid-cols-2">
        <FormSection title="Attribute"
                     description="Änderungen am Wert berechnen Modifikator und Rettungswurf neu. Beide können anschließend manuell überschrieben werden."
                     className="min-w-0">
            <div className="overflow-x-auto pb-1">
                <table className="w-104 table-fixed border-collapse overflow-hidden rounded-md border text-center text-sm">
                    <colgroup>
                        <col className="w-20"/>
                        {attributes.fields.map(attribute => <col key={`column-${attribute.id}`} className="w-14"/>)}
                    </colgroup>
                    <thead className="bg-muted/40">
                    <tr>
                        <th scope="col" className="h-9 border-b border-r px-2 text-left font-medium">Attribut</th>
                        {attributes.fields.map(attribute => {
                            const stat = attribute.stat as AttributeStat;
                            return <th key={`heading-${attribute.id}`} scope="col" title={ATTRIBUTE_LABELS[stat]}
                                       className="h-9 border-b border-r font-semibold uppercase">
                                {stat}
                            </th>;
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    <AttributeTableRow label="Wert">
                        {attributes.fields.map((attribute, index) => {
                            const stat = attribute.stat as AttributeStat;
                            return <FormField key={`value-${attribute.id}`} control={control}
                                              name={`attributes.${index}.value`} render={({field, fieldState}) => (
                                <AttributeCell error={fieldState.error?.message}>
                                    <NumberInput min={1} max={30} {...field} value={field.value ?? ""}
                                                 aria-label={`${ATTRIBUTE_LABELS[stat]} Wert`}
                                                 aria-invalid={fieldState.invalid}
                                                     className="h-8 rounded-none border-0 bg-surface px-1 text-center font-semibold shadow-inner shadow-black/10 focus-visible:ring-2 focus-visible:ring-inset"
                                                 onChange={event => {
                                                     const value = event.target.valueAsNumber;
                                                     field.onChange(value);
                                                     if (!Number.isNaN(value)) {
                                                         const modifier = calculateModifier(value);
                                                         setValue(`attributes.${index}.modifier`, modifier, {shouldDirty: true, shouldValidate: true});
                                                         setValue(`attributes.${index}.save`, modifier, {shouldDirty: true, shouldValidate: true});
                                                     }
                                                 }}/>
                                </AttributeCell>
                            )}/>;
                        })}
                    </AttributeTableRow>

                    <AttributeTableRow label="Mod.">
                        {attributes.fields.map((attribute, index) => {
                            const stat = attribute.stat as AttributeStat;
                            return <AttributeNumberCell key={`modifier-${attribute.id}`}
                                                        name={`attributes.${index}.modifier`}
                                                        label={`${ATTRIBUTE_LABELS[stat]} Modifikator`}/>;
                        })}
                    </AttributeTableRow>

                    <AttributeTableRow label="Rettung" last>
                        {attributes.fields.map((attribute, index) => {
                            const stat = attribute.stat as AttributeStat;
                            return <AttributeNumberCell key={`save-${attribute.id}`}
                                                        name={`attributes.${index}.save`}
                                                        label={`${ATTRIBUTE_LABELS[stat]} Rettungswurf`} lastRow/>;
                        })}
                    </AttributeTableRow>
                    </tbody>
                </table>
            </div>
        </FormSection>

        <FormSection title="Bewegung" contentClassName="grid gap-3 sm:grid-cols-2">
            {(["walk", "fly", "swim", "climb", "burrow"] as const).map(speed => (
                <FormField key={speed} control={control} name={`speeds.${speed}`} render={({field}) => (
                    <FormItem>
                        <label htmlFor={`speed-${speed}`} className="text-sm font-medium">{SPEED_LABELS[speed]}</label>
                        <FormControl><Input id={`speed-${speed}`} {...field}/></FormControl>
                        <FormMessage/>
                    </FormItem>
                )}/>
            ))}
        </FormSection>
    </div>;
}

function AttributeTableRow({label, children, last = false}: {
    label: string;
    children: ReactNode;
    last?: boolean;
}) {
    return <tr>
        <th scope="row" className={`h-8 border-r px-2 text-left font-medium text-muted-foreground ${last ? "" : "border-b"}`}>
            {label}
        </th>
        {children}
    </tr>;
}

function AttributeCell({children, lastRow = false, error}: {
    children: ReactNode;
    lastRow?: boolean;
    error?: string;
}) {
    return <td className={`border-r p-0 align-top ${lastRow ? "" : "border-b"}`}>
        {children}
        {error && <p className="px-0.5 text-[10px] text-destructive">{error}</p>}
    </td>;
}

function AttributeNumberCell({name, label, lastRow = false}: {
    name: `attributes.${number}.modifier` | `attributes.${number}.save`;
    label: string;
    lastRow?: boolean;
}) {
    const {control} = useFormContext<CharacterFormInput>();
    return <FormField control={control} name={name} render={({field, fieldState}) => (
        <AttributeCell lastRow={lastRow} error={fieldState.error?.message}>
            <NumberInput {...field} value={field.value ?? ""} aria-label={label}
                         aria-invalid={fieldState.invalid}
                         className="h-8 rounded-none border-0 bg-surface px-1 text-center shadow-inner shadow-black/10 focus-visible:ring-2 focus-visible:ring-inset"/>
        </AttributeCell>
    )}/>;
}

const ATTRIBUTE_LABELS: Record<AttributeStat, string> = {
    str: "Stärke",
    dex: "Geschicklichkeit",
    con: "Konstitution",
    int: "Intelligenz",
    wis: "Weisheit",
    cha: "Charisma",
};

const SPEED_LABELS = {
    walk: "Laufen",
    fly: "Fliegen",
    swim: "Schwimmen",
    climb: "Klettern",
    burrow: "Graben",
};
